import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import { useTranslation } from "react-i18next";
import { IconTile } from "../../components/ui";
import { DirArrow, PageHero, Section } from "./MarketingParts";

function runtimeConfig() {
  try {
    return window.__AW_RUNTIME_CONFIG__ || {};
  } catch {
    return {};
  }
}

export default function MarketingSupport() {
  const { t } = useTranslation();
  const runtime = runtimeConfig();
  const widgetServerUrl = String(runtime.widgetServerUrl || runtime.apiBaseUrl || window.location.origin).replace(/\/$/, "");
  const demoUrl = `${widgetServerUrl}/widget/demo.html`;

  const faqs = [
    { q: t("mk_sup_1_q"), a: t("mk_sup_1_a") },
    { q: t("mk_sup_2_q"), a: t("mk_sup_2_a", { url: demoUrl, interpolation: { escapeValue: false } }) },
    { q: t("mk_sup_3_q"), a: t("mk_sup_3_a") },
  ];

  return (
    <>
      <PageHero eyebrow={t("mk_support_eyebrow")} icon={<SupportAgentOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />} title={t("m_support_title")} subtitle={t("m_support_subtitle")} />

      <Section maxWidth="md" sx={{ pt: { xs: 5, md: 7 } }}>
        <Typography variant="h2" sx={{ fontSize: { xs: "1.4rem", md: "1.6rem" }, mb: 3 }}>
          {t("mk_support_faq_t")}
        </Typography>
        <Stack spacing={1.25}>
          {faqs.map((f, i) => (
            <Accordion
              key={f.q}
              disableGutters
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: (th) => `${th.aw.radius.md}px !important`,
                "&:before": { display: "none" },
                "&.Mui-expanded": { boxShadow: (th) => th.aw.shadowSm },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreRoundedIcon />}
                id={`sup-h-${i}`}
                aria-controls={`sup-p-${i}`}
                sx={{ px: 2.5, minHeight: 60, "& .MuiAccordionSummary-content": { my: 1.5 } }}
              >
                <Typography component="h3" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                  {f.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails id={`sup-p-${i}`} sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                <Typography sx={{ color: "text.secondary", lineHeight: 1.75, overflowWrap: "anywhere" }}>{f.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>

        <Card
          sx={{
            mt: { xs: 5, md: 6 },
            p: { xs: 3, md: 3.5 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { sm: "center" },
            gap: 2.5,
            bgcolor: (th) => th.aw.softer,
            boxShadow: "none",
          }}
        >
          <IconTile size={48}>
            <MailOutlineRoundedIcon />
          </IconTile>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h2" sx={{ fontSize: "1.15rem" }}>
              {t("mk_support_more_t")}
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: 0.5 }}>{t("mk_support_more_d")}</Typography>
          </Box>
          <Button variant="contained" component={RouterLink} to="/contact" endIcon={<DirArrow />}>
            {t("m_nav_contact")}
          </Button>
        </Card>
      </Section>
    </>
  );
}
